import { useMemo, useState } from 'react';
import type {
  DashboardPeriodMode,
  MarketingDashboardQuery,
  MarketingDashboardResponse,
  MarketingSource,
} from '@troia-v3/types';

import { Icon } from '@/shared/icons';
import { PageToolbar, Breadcrumb, PeriodPicker } from '@/shared/components/ui';
import type { PeriodPickerValue } from '@/shared/components/ui';
import { CampaignsMasterTable } from '../components/CampaignsMasterTable';
import { ChannelFunnelGrid } from '../components/ChannelFunnelGrid';
import { ChannelLeakageTable } from '../components/ChannelLeakageTable';
import { FunnelNarrativeCard } from '../components/FunnelNarrativeCard';
import { LeakageCascadeCard } from '../components/LeakageCascadeCard';
import { MarketingFilterButton, MarketingFilterChips } from '../components/MarketingFiltersBar';
import { MarketingKpiHeroRow } from '../components/MarketingKpiHeroRow';
import type { KpiBadge } from '../components/MarketingKpiHeroRow';
import { useMarketingDashboard } from '../hooks/useMarketingDashboard';

interface CustomRange {
  startDate: string;
  endDate: string;
}

/**
 * Página do dashboard de marketing.
 *
 * 6 widgets + filtros (funnelId, source, via botão "Filtros" + chips) +
 * seletor de período (shared PeriodPicker, F81) na toolbar. Período default = mês.
 * PageToolbar (F23): breadcrumb + label de período à esquerda, período + filtros
 * à direita (período FORA do popover — controle direto do dashboard).
 */
/**
 * Aviso do card de visitantes. Três estados, e a diferença importa:
 *
 *  - **estimado**: não há fonte de visitas medida; o número é `leads × 20`.
 *  - **parcial**: o número é real, mas o período pedido termina depois da
 *    última medição. É o estado NORMAL de um período corrente — o contador de
 *    fluxo físico publica com atraso de horas a dias, então o valor ainda cresce.
 *  - sem badge: medido e completo.
 */
const buildVisitorsBadge = (
  warnings: MarketingDashboardResponse['warnings'],
): KpiBadge | null => {
  if (warnings?.visitorsIsProxy) {
    return {
      label: 'estimado',
      title: 'Número aproximado (≈ leads × 20). Conecte uma fonte de visitas para medir de verdade.',
    };
  }
  if (warnings?.visitorsArePartial) {
    const until = warnings.visitorsCoverageUntil
      ? new Date(warnings.visitorsCoverageUntil).toLocaleString('pt-BR', {
          day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
        })
      : null;
    return {
      label: 'parcial',
      title: until
        ? `Medido até ${until}. A contagem chega com atraso, então este número ainda pode subir.`
        : 'A contagem chega com atraso, então este número ainda pode subir.',
    };
  }
  return null;
};

export const MarketingDashboardPage = () => {
  const now = new Date();

  const [period, setPeriod] = useState<DashboardPeriodMode>('month');
  const [customRange, setCustomRange] = useState<CustomRange | null>(null);
  const [funnelId, setFunnelId] = useState<string | null>(null);
  const [source, setSource] = useState<MarketingSource | null>(null);

  const query = useMemo<MarketingDashboardQuery>(() => {
    const base: MarketingDashboardQuery = { period };
    if (period === 'month' || period === 'year') {
      base.year = now.getUTCFullYear();
      if (period === 'month') {
        base.month = now.getUTCMonth() + 1;
      }
    }
    if (period === 'custom' && customRange) {
      base.startDate = customRange.startDate;
      base.endDate = customRange.endDate;
    }
    if (funnelId) base.funnelId = funnelId;
    if (source) base.source = source;
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, customRange, funnelId, source]);

  const { data, isLoading, isError, error } = useMarketingDashboard(query);

  const handlePeriodChange = (value: PeriodPickerValue): void => {
    setPeriod(value.mode);
    if (value.mode === 'custom' && value.start && value.end) {
      setCustomRange({ startDate: value.start, endDate: value.end });
    }
  };

  const pickerValue: PeriodPickerValue =
    period === 'custom' && customRange
      ? { mode: 'custom', start: customRange.startDate, end: customRange.endDate }
      : { mode: period };

  return (
    <div
      style={{ backgroundColor: 'var(--theme-page-background)', minHeight: '100%' }}
      data-testid="marketing-dashboard-page"
    >
      {/* Toolbar + filterbar (fixos no topo) — PageToolbar do DS (F23) */}
      <div className="sticky top-0 z-20">
        <PageToolbar
          data-testid="marketing-dashboard-toolbar"
          left={
            <>
              <Breadcrumb
                items={[
                  { label: 'Dashboards' },
                  { label: 'Marketing', testid: 'marketing-breadcrumb-current' },
                ]}
                mobileLegend={`Dashboards · ${data?.period.label ?? '…'}`}
                data-testid="marketing-breadcrumb"
              />
              <span
                className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold flex-shrink-0"
                style={{ color: 'var(--theme-card-text)' }}
                data-testid="marketing-period-label"
              >
                <Icon name="calendar" size={13} style={{ color: 'var(--theme-muted-text)' }} />
                {data?.period.label ?? 'Carregando período…'}
              </span>
            </>
          }
          right={
            <div className="flex items-center gap-2 flex-nowrap overflow-x-auto sm:flex-wrap sm:overflow-visible">
              {/* Período — controle direto, shared PeriodPicker (F81) */}
              <PeriodPicker
                value={pickerValue}
                onChange={handlePeriodChange}
                data-testid="marketing-period"
              />
              <MarketingFilterButton
                funnelId={funnelId}
                source={source}
                onFunnelChange={setFunnelId}
                onSourceChange={setSource}
              />
            </div>
          }
          below={
            <MarketingFilterChips
              funnelId={funnelId}
              source={source}
              onFunnelChange={setFunnelId}
              onSourceChange={setSource}
            />
          }
        />
      </div>

      {/* Conteúdo do dashboard */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6 flex flex-col gap-4">
        {isLoading && (
          <div
            className="rounded-xl p-8 text-center"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              border: '1px solid var(--theme-card-border)',
              color: 'var(--theme-muted-text)',
            }}
            data-testid="marketing-dashboard-loading"
          >
            Carregando…
          </div>
        )}

        {isError && (
          <div
            className="rounded-xl p-4"
            style={{
              backgroundColor: 'var(--theme-error-subtle)',
              border: '1px solid var(--theme-card-border)',
              color: 'var(--theme-error-fg)',
            }}
            data-testid="marketing-dashboard-error"
          >
            Erro: {String((error as Error)?.message ?? 'desconhecido')}
          </div>
        )}

        {!isLoading && !isError && data && (
          <>
            <MarketingKpiHeroRow
              kpis={data.kpis}
              visitorsBadge={buildVisitorsBadge(data.warnings)}
            />
            <FunnelNarrativeCard narrative={data.funnelNarrative} />
            <ChannelFunnelGrid
              channels={data.channelFunnels}
              noAdSpendData={data.warnings?.noAdSpendData ?? false}
            />
            <CampaignsMasterTable
              rows={data.campaignsMaster}
              noAdSpendData={data.warnings?.noAdSpendData ?? false}
            />
            <LeakageCascadeCard
              cascade={data.leakageCascade}
              reasonsAreSynthetic={data.warnings?.leakageReasonsAreSynthetic ?? false}
            />
            <ChannelLeakageTable channels={data.channelLeakage} />
          </>
        )}
      </div>
    </div>
  );
};
